import {
  AppError,
  Button,
  Loader,
  SidebarOverlay,
  Tag,
  tabLinkClassName,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import type { TFunction } from "i18next";
import { useDeliverApi } from "@bfc-software/bfc-web-ui/lib/deliver-api-client";

import {
  DEFAULT_DRAWER_TAB,
  DRAWER_TABS,
  type DrawerTab,
  type OpenRoute,
} from "../-data/filters";

import { SummaryTab } from "./drawer-summary";
import {
  ItemsTab,
  PiecesTab,
  ProofTab,
  ReturnsTab,
  StopsTab,
  type TabProps,
} from "./drawer-tabs";
import styles from "./route-drawer.module.css";

type Props = {
  route: OpenRoute;
  tab: DrawerTab | undefined;
  stop: number | undefined;
  lang: string;
  t: TFunction;
  onTabChange: (tab: DrawerTab) => void;
  onStopChange: (stop: number | undefined) => void;
  /** Jump to the Items tab already narrowed to one stop, in one navigation. */
  onViewStop: (stop: number) => void;
  onClose: () => void;
};

/** Tabs whose endpoint takes a stop filter; Stops and Returns do not. */
const STOP_FILTERED: ReadonlySet<DrawerTab> = new Set([
  "items",
  "pieces",
  "proof",
]);

/**
 * The route drawer: one route, drilled into by stop, item, piece, return and
 * proof of delivery.
 *
 * Which route is open is a URL fact (`open`), not component state, so a reload
 * or a pasted link reopens it -- `SidebarOverlay` in its `external` arm draws
 * no trigger and keeps no open/closed state of its own, and every dismissal
 * routes back out through `onClose`. The header comes from the route's own
 * detail read rather than the list row, because on a cold load there is no
 * list row to borrow from.
 */
export function RouteDrawer({
  route,
  tab,
  stop,
  lang,
  t,
  onTabChange,
  onStopChange,
  onViewStop,
  onClose,
}: Props) {
  const client = useDeliverApi();
  const detail = client.useQuery("get", "/app/deliver/api/routes/{routeId}", {
    params: {
      path: { routeId: route.routeId },
      query: { warehouse: route.warehouse },
    },
  });

  const active = tab ?? DEFAULT_DRAWER_TAB;
  const row = detail.data;
  const tabProps: TabProps = {
    warehouse: route.warehouse,
    routeId: route.routeId,
    stop: STOP_FILTERED.has(active) ? stop : undefined,
    lang,
    t,
  };
  // A tab remounts when its scope changes, so its page counter starts over.
  const tabKey = `${active}:${tabProps.stop ?? "all"}`;

  return (
    <SidebarOverlay
      control="external"
      // The tabs are wide tables; half the window gives them room to read.
      width="half"
      isOpen={true}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      header={
        <>
          <h2 className="drawer-title">
            {row
              ? t("drawer.title", { id: route.routeId, route: row.route })
              : route.routeId}
          </h2>
          {row ? (
            <div className="mt-4 flex flex-wrap items-center gap-12">
              <Tag
                label={row.deliveryStatus}
                display={{
                  tone: /complete/i.test(row.deliveryStatus)
                    ? "success"
                    : "info",
                }}
              />
              <span className="drawer-subtitle">
                {t("drawer.meta.driver", {
                  value: row.driverName || row.driver,
                })}
              </span>
              <span className="drawer-subtitle">
                {t("drawer.meta.truck", { value: row.truck || t("none") })}
              </span>
              <span className="drawer-subtitle">
                {t("drawer.meta.warehouse", { value: row.warehouse })}
              </span>
              <span className="drawer-subtitle">
                {t("drawer.meta.stops", {
                  done: row.stopsDelivered,
                  total: row.stops,
                })}
              </span>
            </div>
          ) : null}
        </>
      }
    >
      <div className="drawer-content" data-test-role="route-drawer">
        {detail.isLoading ? (
          <div className={styles.state} data-test-role="route-drawer-loading">
            <Loader type="inline" />
          </div>
        ) : detail.isError || !row ? (
          <div className={styles.state} data-test-role="route-drawer-failed">
            <AppError
              description={t("drawer.failed")}
              resolution={t("list.failureSupport")}
            />
          </div>
        ) : (
          <>
            <nav aria-label={t("drawer.tabsAria")} className={styles.tabs}>
              {DRAWER_TABS.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={tabLinkClassName(id === active)}
                  aria-current={id === active ? "page" : undefined}
                  onClick={() => onTabChange(id)}
                >
                  {t(`drawer.tabs.${id}`)}
                </button>
              ))}
            </nav>

            {stop !== undefined && STOP_FILTERED.has(active) ? (
              <div className={styles.filter}>
                <Tag
                  label={t("drawer.stopFilter", { stop })}
                  display={{ tone: "accent" }}
                />
                <Button
                  kind="link"
                  variant="accent"
                  text={t("drawer.clearStop")}
                  onPress={() => onStopChange(undefined)}
                />
              </div>
            ) : null}

            <div className={styles.body}>
              {active === "summary" ? (
                <SummaryTab row={row} lang={lang} t={t} />
              ) : active === "stops" ? (
                <StopsTab key={tabKey} {...tabProps} onViewItems={onViewStop} />
              ) : active === "items" ? (
                <ItemsTab key={tabKey} {...tabProps} />
              ) : active === "pieces" ? (
                <PiecesTab key={tabKey} {...tabProps} />
              ) : active === "returns" ? (
                <ReturnsTab key={tabKey} {...tabProps} />
              ) : (
                <ProofTab key={tabKey} {...tabProps} />
              )}
            </div>
          </>
        )}
      </div>
    </SidebarOverlay>
  );
}
